import {
  AddressCreateParams,
  CarryingHelp,
  ContactCreateParams,
  DateTimePeriodParams,
  GeoAutocompleteAddress,
  InstructionSuspicion,
  InsuranceTier,
  ProductSelectionPresetSearchResult,
  ProductSelectionResult,
  Quote,
  StopSituation,
} from '@brenger/api-client';
import { UploadedDocument } from '../../brenger-shared-ui';
import { StopType } from '../../types';
import { TransformedLot } from '../../utils/tbAuctionsMethods';
import { AuctionType } from './components/SubStepAuctionTypes';
import { FloorChoice } from './components/SubStepFloor';
import { HomeSituationType } from './components/SubStepHomeSituation';
import { ExpectedUtmParams, ItemWithPSAndLot } from './interface';

export enum GeneralFlowActionTypes {
  SET_STEP_LOADING = `@@GeneralFlow/SET_STEP_LOADING`,
  SET_DESTINATION_DETAILS = `@@GeneralFlow/SET_DESTINATION_DETAILS`,
  SET_DESTINATION_META = `@@GeneralFlow/SET_DESTINATION_META`,
  RESET_DESTINATION_DETAILS = `@@GeneralFlow/RESET_DESTINATION_DETAILS`,
  POP_FOOTER_NOTIFICATION = `@@GeneralFlow/POP_FOOTER_NOTIFICATION`,
  HIDE_FOOTER_NOTIFICATION = `@@GeneralFlow/HIDE_FOOTER_NOTIFICATION`,
  SET_SUBMITTING_STATUS = `@@GeneralFlow/SET_SUBMITTING_STATUS`,
  SET_PRICE_LOADING = `@@GeneralFlow/SET_PRICE_LOADING`,
  SET_DISCOUNT_PERCENTAGE_BASIS_POINTS = `@@GeneralFlow/SET_DISCOUNT_PERCENTAGE_BASIS_POINTS`,
  RESET_PRICE = `@@GeneralFlow/RESET_PRICE`,
  PUSH_NOTIFICATION = `@@GeneralFlow/PUSH_NOTIFICATION`,
  SET_PROGRESS_STEP = `@@GeneralFlow/SET_PROGRESS_STEP`,
  TOGGLE_SHEET = `@@GeneralFlow/TOGGLE_SHEET`,
  CREATE_TRANSPORT_REQUEST_START = `@@GeneralFlow/CREATE_TRANSPORT_REQUEST_START`,
  CREATE_TRANSPORT_REQUEST_FAILURE = `@@GeneralFlow/CREATE_TRANSPORT_REQUEST_FAILURE`,
  RESET_FLOW = `@@GeneralFlow/RESET_FLOW`,
  SET_TRANSPORT_UUID = `@@GeneralFlow/SET_TRANSPORT_UUID`,
  SET_REFERRER = `@@GeneralFlow/SET_REFERRER`,
  SET_QUOTE = `@@GeneralFlow/SET_QUOTE`,
  ITEM_SETS_CHANGE = `@@GeneralFlow/ITEM_SETS_CHANGE`,
  PRICE_API_RETRY_ATTEMPT_INCREMENT = `@@GeneralFlow/PRICE_API_RETRY_ATTEMPT_INCREMENT`,
  RESET_RETRY_ATTEMPTS = `@@GeneralFlow/RESET_RETRY_ATTEMPTS`,
  GET_PRICE = `@@GeneralFlow/GET_PRICE`,
  POST_DRAFT_QUOTES = `@@GeneralFlow/POST_DRAFT_QUOTES`,
  ADD_LAST_VISIT_TIME = `@@GeneralFlow/ADD_LAST_VISIT_TIME`,
  LOCALITY_CHANGE = `@@GeneralFlow/LOCALITY_CHANGE`,
  SET_INSTRUCTION_SUSPICION = `@@GeneralFlow/SET_INSTRUCTION_SUSPICION`,
  SET_SHOW_TIME_MODAL = `@@GeneralFlow/SET_SHOW_TIME_MODAL`,
  SET_SHOW_FLEX_DATES_MODAL = `@@GeneralFlow/SET_SHOW_FLEX_DATES_MODAL`,
  SHOW_MARKTPLAATS_WELCOME_MODAL = `@@GeneralFlow/SHOW_MARKTPLAATS_WELCOME_MODAL`,
  RESET_ERROR = `@@GeneralFlow/RESET_ERROR`,
  ITEMS_SHOW_MODAL = `@@GeneralFlow/ITEMS_SHOW_MODAL`,
  SET_TR_VALIDATION_ERROR = `@@GeneralFlow/SET_TR_VALIDATION_ERROR`,
  SET_QUERY_PS = `@@GeneralFlow/SET_QUERY_PS`,
  SET_SEARCH_RESULT_PS = `@@GeneralFlow/SET_SEARCH_RESULT_PS`,
  ADD_ITEM_PS = `@@GeneralFlow/ADD_ITEM_PS`,
  REMOVE_ITEM_PS = `@@GeneralFlow/REMOVE_ITEM_PS`,
  UPDATE_ITEM_PS = `@@GeneralFlow/UPDATE_ITEM_PS`,
  SET_PREFILL_PS = `@@GeneralFlow/SET_PREFILL_PS`,
  SET_CONTACT_IS_BUSINESS = `@@GeneralFlow/SET_CONTACT_IS_BUSINESS`,
  SET_CONTACT_DETAILS = `@@GeneralFlow/SET_CONTACT_DETAILS`,
  SET_COMPANY_DETAILS = `@@GeneralFlow/SET_COMPANY_DETAILS`,
  SET_DEFERRED_VAT = `@@GeneralFlow/SET_DEFERRED_VAT`,
  SET_STOP_ADDRESS = `@@GeneralFlow/SET_STOP_ADDRESS`,
  SET_HELP = `@@GeneralFlow/SET_HELP`,
  SET_UTM_DETAILS = `@@GeneralFlow/SET_UTM_DETAILS`,
  SET_CHOICE = `@@GeneralFlow/SET_CHOICE`,
  RESET_CHOICE = `@@GeneralFlow/RESET_CHOICE`,
  SET_AB = `@@GeneralFlow/SET_AB`,
  RESET_AB = `@@GeneralFlow/RESET_AB`,
}

export enum AccountType {
  INDIVIDUAL = 'individual',
  BUSINESS = 'business',
}

export interface SetQuoteAction {
  type: GeneralFlowActionTypes.SET_QUOTE;
  payload: {
    quote: Quote;
    removePrice: boolean;
  };
}

export interface SetDiscountPercentageBasisPointsAction {
  type: GeneralFlowActionTypes.SET_DISCOUNT_PERCENTAGE_BASIS_POINTS;
  payload: number | null;
}

export interface ToggleStepLoadingAction {
  type: GeneralFlowActionTypes.SET_STEP_LOADING;
  payload: boolean;
}

export interface SetDestinationDetailsAction {
  type: GeneralFlowActionTypes.SET_DESTINATION_DETAILS;
  payload: GeoAutocompleteAddress | null;
  stopType: StopType;
}
export interface SetDestinationMetaAction {
  type: GeneralFlowActionTypes.SET_DESTINATION_META;
  payload: null | {
    utm_account?: string;
  };
  stopType: StopType;
}

export interface ResetFlowAction {
  type: GeneralFlowActionTypes.RESET_FLOW;
  payload: {
    resetDestination: boolean;
  };
}

export interface GeneralFlowAction {
  type: GeneralFlowActionTypes.ADD_LAST_VISIT_TIME | GeneralFlowActionTypes.POST_DRAFT_QUOTES;
}

export interface ItemsShowModalAction {
  type: GeneralFlowActionTypes.ITEMS_SHOW_MODAL;
  payload: boolean;
}

export interface SetLocalityChangePayload {
  stopType: StopType;
  locality_old: string;
  locality_new: string;
  quote_old: Quote | null;
  above_max_distance_old: boolean;
  above_max_date_distance_old: boolean;
  date_selection_not_available_old: boolean;
  is_flex_dates_old: boolean;
}
export interface SetLocalityChangeAction {
  type: GeneralFlowActionTypes.LOCALITY_CHANGE;
  payload: SetLocalityChangePayload | null;
}

export interface SetShowModalAction {
  type:
    | GeneralFlowActionTypes.SET_SHOW_TIME_MODAL
    | GeneralFlowActionTypes.SET_SHOW_FLEX_DATES_MODAL
    | GeneralFlowActionTypes.SHOW_MARKTPLAATS_WELCOME_MODAL;
  payload: boolean;
}

export interface SetInstructionSuspicionPayload {
  suspicions: InstructionSuspicion[] | null;
  stopType: 'pickup' | 'delivery';
}
export interface SetInstructionSuspicionAction {
  type: GeneralFlowActionTypes.SET_INSTRUCTION_SUSPICION;
  payload: SetInstructionSuspicionPayload;
}

export type TransportRequestValidationError =
  | 'selected_date_not_available'
  | 'selected_time_not_available'
  | 'should_select_date'
  | 'should_select_time'
  | 'failed_price_request'
  | 'same_address_for_pickup_delivery'
  | 'pickup_has_invalid_coordinates'
  | 'delivery_has_invalid_coordinates';

export interface SetTRValidationErrorAction {
  type: GeneralFlowActionTypes.SET_TR_VALIDATION_ERROR;
  payload: TransportRequestValidationError | null;
}

export interface SetQueryPsAction {
  type: GeneralFlowActionTypes.SET_QUERY_PS;
  payload: string | null;
}

export interface SetSearchResultPsAction {
  type: GeneralFlowActionTypes.SET_SEARCH_RESULT_PS;
  payload:
    | Partial<ProductSelectionResult & { auctionLot?: TransformedLot }>
    | (ProductSelectionPresetSearchResult & { auctionLot?: TransformedLot })
    | null;
}
export interface AddItemPsAction {
  type: GeneralFlowActionTypes.ADD_ITEM_PS;
  payload: ItemWithPSAndLot;
}
export interface RemoveItemPsAction {
  type: GeneralFlowActionTypes.REMOVE_ITEM_PS;
  payload:
    | {
        uuid: string;
      }
    | {
        all: boolean;
      };
}
export interface UpdateItemPsAction {
  type: GeneralFlowActionTypes.UPDATE_ITEM_PS;
  payload: {
    uuid: string;
    item: ItemWithPSAndLot;
  };
}

export type PsPrefillOtherActionType = 'CONFIG' | 'SEARCH';
export type PsPrefillProductActionType = 'PREFILL';
export type PsPrefillActionType = PsPrefillOtherActionType | PsPrefillProductActionType;

export interface PsPrefillOtherAction {
  /** what to do?
   * 1) CONFIG - select product and make the user start at config screen
   * 2) SEARCH - only set the search query, make the user start at the search screen
   */
  type: PsPrefillOtherActionType;
  /**
   * Value can be a product IRI or Value for search
   */
  value: string;
}
export interface PSPrefillProductAction {
  /**
   * Add product to item set, user ends up at items screen
   */
  type: PsPrefillProductActionType;
  /**
   * Value is a product IRI
   */
  value: string;
  /**
   * Should the user be warned beforeabout a prefilled item, before continueing in the flow?
   */
  shouldWarn: boolean;
}

export interface SetPrefillPsAction {
  type: GeneralFlowActionTypes.SET_PREFILL_PS;
  payload: PsPrefillOtherAction | PSPrefillProductAction | null;
}

export type ContactType = 'customer' | 'pickup' | 'delivery';

export interface SetContactDetailsAction {
  type: GeneralFlowActionTypes.SET_CONTACT_DETAILS;
  payload: {
    contact_type: ContactType;
    details: ContactCreateParams | null;
  };
}

export interface CompanyDetails {
  company_name: string;
  coc_number: string;
  vat_number: string;
  address: Pick<AddressCreateParams, 'line1' | 'postal_code' | 'locality' | 'country_code'>;
}

export interface ContactCustomer {
  contact_details?: ContactCreateParams;
  company_details?: Partial<CompanyDetails>;
  is_business?: boolean;
}
export interface ContactStop {
  contact_details: ContactCreateParams;
  address?: AddressCreateParams | null;
  instructions?: string;
  address_is_prefilled?: boolean;
}

export interface SetContactIsBusinessAction {
  type: GeneralFlowActionTypes.SET_CONTACT_IS_BUSINESS;
  payload: boolean;
}
export interface SetCompanyDetailsAction {
  type: GeneralFlowActionTypes.SET_COMPANY_DETAILS;
  payload: Partial<CompanyDetails> | null;
}

/**
 * Deferred (reverse-charge) VAT eligibility of a guest company, as answered by Core.
 * Keeps the VAT number + country it was answered for, so a stale answer never applies to edited details.
 */
export interface DeferredVatState {
  loading: boolean;
  eligible: boolean;
  vat_number: string;
  country_code: string;
}

export interface SetDeferredVatAction {
  type: GeneralFlowActionTypes.SET_DEFERRED_VAT;
  payload: DeferredVatState;
}

export interface SetStopAddressAction {
  type: GeneralFlowActionTypes.SET_STOP_ADDRESS;
  payload: {
    stopType: 'pickup' | 'delivery';
    address?: AddressCreateParams | null;
    instructions?: string;
    address_is_prefilled?: boolean;
  };
}
export interface SetUtmDetails {
  type: GeneralFlowActionTypes.SET_UTM_DETAILS;
  payload: null | { [key in ExpectedUtmParams]?: string };
}

export interface GfChoices {
  situation?: StopSituation;
  home_situation?: HomeSituationType;
  carrying_help?: CarryingHelp;
  product_payment_otp_in?: boolean;
  product_payment_amount?: string;
  auction_type?: AuctionType;
  pickup_floor?: FloorChoice;
  delivery_floor?: FloorChoice;
  pickup_date?: string;
  delivery_date?: string;
  different_delivery_date?: boolean;
  pickup_time?: DateTimePeriodParams;
  delivery_time?: DateTimePeriodParams;
  terms?: boolean;
  terms_equipment?: boolean;
  newsletter?: boolean;
  invoices?: UploadedDocument[];
  client_reference?: string;
  insurance_tier?: InsuranceTier;
}

export type GfChoicesKey = keyof GfChoices;

export interface SetChoiceAction {
  type: GeneralFlowActionTypes.SET_CHOICE;
  payload: GfChoices;
}

export interface ResetChoiceAction {
  type: GeneralFlowActionTypes.RESET_CHOICE;
  payload: GfChoicesKey[] | 'ALL';
}
